import { useContext, useMemo } from "react";
import { SummaryReportDashboardContext } from "contexts/SummaryReportDashboardContext/SummaryReportDashboardContext";
import ReportDowntimeBarChart from "components/ReportDowntimeBarChart";
import { useParams } from "react-router-dom";
import { DefaultTranslationFieldsInTable, DisplayInOptionsEnum, SummaryOption } from "constants/constants";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { useStyles } from "../DowntimeSummaryByFailureType/DowntimeSummaryByFailureType.styles";
import { IDowntimeTotalsSummarized } from "../TotalDowntimeByMachineCenter/TotalDowntimeByMachineCenterInterfaces";
import { percentageChecker } from "utilities/utilities";
import { translateDefaultEntity } from "utilities/localesUtilities";
import NoData from "components/NoData/NoData.view";
import { CircularProgress } from "@mui/material";
import { MillDashboardContext } from "contexts/MillDashboardsContext/MillDashboardsContext";
import { IMillReportCardProps } from "../../../MillDashboardInterfaces";
import { useTranslation } from "react-i18next";

const DowntimeFailureTypeByMachineCenter = ({ countryCode }: IMillReportCardProps) => {
    const classes = useStyles();
    const { millId } = useParams();
    const { selectedFailureType, filters, totalDowntimeSeconds } = useContext(SummaryReportDashboardContext);
    const { i18n } = useTranslation();

    const {
        displayUnit: displayIn,
        reportRangeEndDate: endDate,
        reportRangeStartDate: startDate,
    } = useContext(MillDashboardContext);

    const { downtimeSummaryPerMachineCenter, loading } =
        MillsEntity.GetTotalDowntimeSummaryByTypePerMachineCenterMillDashboard(
            { ...filters, startDate, endDate, displayIn },
            SummaryOption.BY_TYPE,
            +millId!,
            Number(selectedFailureType?.id),
        );

    const downtimeSummaryFormatted = useMemo(
        () =>
            downtimeSummaryPerMachineCenter.reduce((acc: IDowntimeTotalsSummarized[], obj) => {
                const machineCenterId = obj.machine_center.machine_center_id;
                const existingMachineCenterIndex = acc.findIndex(
                    (summary) => summary.machineCenterId === machineCenterId,
                );

                const defaultFailureTypeTranslatedName = translateDefaultEntity(
                    i18n.language,
                    obj.failure_type,
                    "name",
                    DefaultTranslationFieldsInTable.FAILURE_TYPE,
                );

                if (!(existingMachineCenterIndex >= 0)) {
                    acc = [
                        ...acc,
                        {
                            machineCenterId: obj.machine_center.machine_center_id,
                            name: obj.machine_center.short_name || obj.machine_center.name,
                            typeTotals: [
                                {
                                    name: defaultFailureTypeTranslatedName,
                                    color_code: obj.failure_type.color_code,
                                    total_seconds: obj.total_seconds,
                                    total_occurrences: obj.total_occurrences,
                                    default_failure_type_translations:
                                        obj.failure_type.default_failure_type_translations,
                                },
                            ],
                        },
                    ];
                } else {
                    const existingFailureTypeIndex = acc[existingMachineCenterIndex].typeTotals.findIndex(
                        (summary) => summary.name === defaultFailureTypeTranslatedName,
                    );

                    if (!(existingFailureTypeIndex >= 0)) {
                        acc[existingMachineCenterIndex] = {
                            ...acc[existingMachineCenterIndex],
                            typeTotals: [
                                ...acc[existingMachineCenterIndex].typeTotals,
                                {
                                    name: defaultFailureTypeTranslatedName,
                                    color_code: obj.failure_type.color_code,
                                    total_seconds: obj.total_seconds,
                                    total_occurrences: obj.total_occurrences,
                                    default_failure_type_translations:
                                        obj.failure_type.default_failure_type_translations,
                                },
                            ],
                        };
                    } else {
                        acc[existingMachineCenterIndex].typeTotals[existingFailureTypeIndex] = {
                            ...acc[existingMachineCenterIndex].typeTotals[existingFailureTypeIndex],
                            total_seconds:
                                acc[existingMachineCenterIndex].typeTotals[existingFailureTypeIndex].total_seconds +
                                obj.total_seconds,
                            total_occurrences:
                                acc[existingMachineCenterIndex].typeTotals[existingFailureTypeIndex].total_occurrences +
                                obj.total_occurrences,
                        };
                    }
                }
                return acc;
            }, []),
        [downtimeSummaryPerMachineCenter, i18n.language],
    );

    const graphData = useMemo(
        () =>
            downtimeSummaryFormatted
                .map((downtimeSummary) => {
                    let data: { [key: string]: string | number } = {
                        machineCenterId: downtimeSummary.machineCenterId,
                        machineCenter: downtimeSummary.name,
                        countryCode: countryCode as string,
                    };
                    downtimeSummary.typeTotals.forEach((total) => {
                        const defaultFailureTypeTranslatedName = translateDefaultEntity(
                            i18n.language,
                            total,
                            "name",
                            DefaultTranslationFieldsInTable.FAILURE_TYPE,
                        );
                        const secondsToMinutes =
                            Math.round(total.total_seconds / 60) < 1 ? 1 : Math.round(total.total_seconds / 60);
                        data = {
                            ...data,
                            [`${defaultFailureTypeTranslatedName}`]:
                                displayIn === DisplayInOptionsEnum.MINUTES
                                    ? secondsToMinutes
                                    : displayIn === DisplayInOptionsEnum.PERCENT
                                    ? totalDowntimeSeconds
                                        ? (100 * total.total_seconds) / totalDowntimeSeconds
                                        : 0
                                    : total.total_occurrences,
                            [`${defaultFailureTypeTranslatedName}Color`]: total.color_code,
                        };
                    });

                    const totalDowntime = downtimeSummary.typeTotals.reduce(
                        (acc, obj) =>
                            displayIn === DisplayInOptionsEnum.MINUTES
                                ? (Math.round(obj.total_seconds / 60) < 1 ? 1 : Math.round(obj.total_seconds / 60)) +
                                  acc
                                : displayIn === DisplayInOptionsEnum.PERCENT
                                ? (totalDowntimeSeconds ? (100 * obj.total_seconds) / totalDowntimeSeconds : 0) + acc
                                : obj.total_occurrences + acc,
                        0,
                    );
                    data = {
                        ...data,
                        totalDowntimeByMill:
                            displayIn === DisplayInOptionsEnum.PERCENT
                                ? percentageChecker(totalDowntime)
                                : totalDowntime,
                    };

                    return data;
                })
                .sort((a, b) => Number(a.totalDowntimeByMill) - Number(b.totalDowntimeByMill))
                .slice(-5),
        [downtimeSummaryFormatted, countryCode, displayIn, i18n.language, totalDowntimeSeconds],
    );

    return loading ? (
        <div className={classes.noData}>
            <CircularProgress size={24} color="info" />
        </div>
    ) : graphData.length > 0 ? (
        <ReportDowntimeBarChart
            data={graphData}
            noPercent={displayIn !== DisplayInOptionsEnum.PERCENT}
            index="machineCenter"
            noTrend
            noLegend
        />
    ) : (
        <NoData className={classes.noData} />
    );
};

export default DowntimeFailureTypeByMachineCenter;
